import "./CalculatorPage.scss";
// REACT; REDUX; ROUTER
import { useEffect, useState } from "react";

// COMPONENTS & FUNCTIONS
import CalculatorUnit from "../components/units/CalculatorUnit";
import AdvancedCalculatorUnit from "../components/units/AdvancedCalculatorUnit";
import ConverterUnit from "../components/units/ConverterUnit";
import SearchUnitTool from "../components/tools/SearchUnitTool";
import ReceiptTool from "../components/tools/ReceiptTool";
import ImportCalculatorsTool from "../components/tools/ImportCalculatorsTool";
import {
  createCalculator,
  duplicateCalculator,
} from "../functions/unitHandler";
import {
  createAdvancedCalculator,
  duplicateAdvancedCalculator,
  isAdvancedCalculatorName,
} from "../functions/advancedCalculatorHandler";
import MessageBoxTool from "../components/tools/MessageBoxTool";
import {
  addAppDataToLocalStorage,
  deleteAllOfTypeFromLocalStorage,
  getAppDataFromLocalStorage,
  removeAppDataFromLocalStorage,
  replaceAllAppDataInLocalStorage,
} from "../functions/localStorageHandlers";

// SVG & IMAGES
import calcHeaderFigure from "../img/header-calculator.svg";
import * as FaIcons from "react-icons/fa";
import curvedArrowImg from "../img/general-curved-arrow.png";

// INTERFACES
import { AppData } from "../interface/functionalityInterface";
import {
  AdvancedCalculatorUnitProps,
  CalculatorPageProps,
  CalculatorUnitProps,
  ConverterUnitProps,
  ReceiptUnitProps,
} from "../interface/propTypesInterface";

/*
CALCULATOR PAGE COMPONENT INFORMATION
  Every action with the calculators are saved in state as well as in local storage. Thus state remains even after restart or shutdown.
*/

export default function CalculatorPage(props: CalculatorPageProps) {
  // ::::: USE HOOKS :::::
  // meBUG order of calculators mixed when retrieved from local storage

  // Start this component by bringing in the calculators from the local storage
  useEffect(() => {
    handleStart();
  }, []);

  // ::::: STATES  ::::::
  const [units, setUnits] = useState<
    (
      | CalculatorUnitProps
      | AdvancedCalculatorUnitProps
      | ConverterUnitProps
    )[]
  >([]);
  const [receipts, setReceipts] = useState<ReceiptUnitProps[]>([]);

  // ::::: HANDLER FUNCTIONS :::::
  const handleStart = () => {
    const allUnits = getAppDataFromLocalStorage([
      AppData.CALCULATOR,
      AppData.ADVANCED_CALCULATOR,
      AppData.CONVERTER,
    ]) as (
      | CalculatorUnitProps
      | AdvancedCalculatorUnitProps
      | ConverterUnitProps
    )[];
    const allReceipts = getAppDataFromLocalStorage([
      AppData.RECEIPT,
    ]) as ReceiptUnitProps[];
    if (allUnits) setUnits(allUnits);
    if (allReceipts) setReceipts(allReceipts);
  };

  // Callback from SearchUnitTool component. Set unit in state and save in local storage
  // state from unit doesnt trigger state in CalculatorPage,
  // thus the latest values need to be retrieved from the local Storage
  const handleCreateUnit = (unitName: string) => {
    const allUnits = getAppDataFromLocalStorage([
      AppData.CALCULATOR,
      AppData.ADVANCED_CALCULATOR,
      AppData.CONVERTER,
    ]) as (
      | CalculatorUnitProps
      | AdvancedCalculatorUnitProps
      | ConverterUnitProps
    )[];
    if (!allUnits) return;
    let newUnit:
      | CalculatorUnitProps
      | AdvancedCalculatorUnitProps
      | ConverterUnitProps;
    newUnit = isAdvancedCalculatorName(unitName)
      ? createAdvancedCalculator(unitName)
      : createCalculator(unitName);
    const unitsWithAddedUnit = [...allUnits, newUnit];
    addAppDataToLocalStorage(newUnit);
    setUnits(unitsWithAddedUnit);
    props.showNotification({
      message: `Added ${newUnit.name}`,
      type: "SUCCESS",
    });
  };

  // Callback from CalculatorUnit component. Add a remove class animation and wait, then remove from state and local storage.
  const handleRemoveUnit = (unitID: string) => {
    const filteredUnits = units.filter((unit) => unit.id !== unitID);
    // If you find the element add a remove animation
    const unitElem = document.getElementById(unitID);
    if (unitElem) {
      unitElem.classList.remove("show");
    }
    setTimeout(() => {
      setUnits(filteredUnits);
    }, 300);
    removeAppDataFromLocalStorage(unitID);
  };

  // Callback from CalculatorUnit component. Gets the calc from component, duplicates it and gives unique ID.
  // state from CalculatorUnit doesnt trigger state in CalculatorPage,
  // thus the latest values need to be retrieved from the local Storage
  const handleDuplicateUnit = (
    originalUnit:
      | CalculatorUnitProps
      | AdvancedCalculatorUnitProps
      | ConverterUnitProps
  ) => {
    const allUnits = getAppDataFromLocalStorage([
      AppData.CALCULATOR,
      AppData.ADVANCED_CALCULATOR,
      AppData.CONVERTER,
    ]) as (
      | CalculatorUnitProps
      | AdvancedCalculatorUnitProps
      | ConverterUnitProps
    )[];
    if (!allUnits) return;
    let duplicatedUnit:
      | CalculatorUnitProps
      | AdvancedCalculatorUnitProps
      | ConverterUnitProps;
    switch (originalUnit.type) {
      case AppData.ADVANCED_CALCULATOR:
        duplicatedUnit = duplicateAdvancedCalculator(originalUnit);
        break;

      default:
        duplicatedUnit = duplicateCalculator(originalUnit);
        break;
    }
    addAppDataToLocalStorage(duplicatedUnit);
    setUnits([...allUnits, duplicatedUnit]);
    props.showNotification({
      message: `Duplicated ${originalUnit.name}`,
      type: "SUCCESS",
    });
  };

  // Uses state to find localstorage key names and deletes all calcs from localstorage. Then updates the page to reset state.
  const handleDeleteAllUnits = (confirm: boolean) => {
    if (!confirm) {
      return;
    }
    if (units.length < 1) {
      props.showNotification({
        message: "There are no units to delete",
        type: "ERROR",
      });
      return;
    }
    deleteAllOfTypeFromLocalStorage([
      AppData.CALCULATOR,
      AppData.ADVANCED_CALCULATOR,
      AppData.CONVERTER,
    ]);
    setUnits([]);
    props.showNotification({
      message: "Deleted all calculators",
      type: "WARNING",
    });
  };

  const handleExportAll = (confirm: boolean) => {
    if (!confirm) {
      return;
    }

    const appdata = localStorage.getItem("appdata");
    if (appdata === null) {
      props.showNotification({
        message: "Unknown error when exporting data",
        type: "ERROR",
      });
      return;
    }

    const parsedAppdata = JSON.parse(appdata);

    const element = document.createElement("a");
    const file = new Blob([JSON.stringify(parsedAppdata, null, 2)], {
      type: "text/plain",
    });
    element.href = URL.createObjectURL(file);
    element.download = "abacalc-export.json";
    element.click();

    props.showNotification({
      message: "Successfully exported data",
      type: "SUCCESS",
    });
  };

  const handleImportAll = (
    data: (
      | ReceiptUnitProps
      | CalculatorUnitProps
      | AdvancedCalculatorUnitProps
      | ConverterUnitProps
    )[]
  ) => {
    replaceAllAppDataInLocalStorage(data);
    handleStart();

    props.showNotification({
      message: "Successfully imported data",
      type: "SUCCESS",
    });
  };

  // Callback from CalculatorUnit component. Get formatted receipt and add to state and local storage.
  const handleAddToReceipt = (receipt: ReceiptUnitProps | null) => {
    if (receipt !== null) {
      addAppDataToLocalStorage(receipt);
      handleStart();
      props.showNotification({
        message: `Added ${receipt.label} calculation to receipt`,
        type: "SUCCESS",
      });
    } else {
      props.showNotification({
        message: "Calculator empty",
        type: "WARNING",
      });
    }
  };

  const handleDeleteAllReceipts = () => {
    setReceipts([]);
    deleteAllOfTypeFromLocalStorage([AppData.RECEIPT]);
  };

  const handleDeleteReceipt = (id: string) => {
    setReceipts(receipts.filter((receipt) => receipt.id !== id));
    removeAppDataFromLocalStorage(id);
  };

  // meBUG: This is being rerendered 2 times at start
  // Create JSX components for either CalculatorUnit or ConverterUnit
  const jsxUnits = units.map((state) => {
    return state.type === AppData.ADVANCED_CALCULATOR ? (
      <AdvancedCalculatorUnit
        key={state.id}
        {...(state as AdvancedCalculatorUnitProps)}
        removeUnit={handleRemoveUnit}
        duplicateUnit={handleDuplicateUnit}
        addToReceipt={handleAddToReceipt}
        dragDrop={() => {}}
      />
    ) : state.type === AppData.CALCULATOR ? (
      <CalculatorUnit
        key={state.id}
        {...(state as CalculatorUnitProps)}
        removeUnit={handleRemoveUnit}
        duplicateUnit={handleDuplicateUnit}
        addToReceipt={handleAddToReceipt}
        dragDrop={() => {}}
      />
    ) : state.type === AppData.CONVERTER ? (
      <ConverterUnit
        key={state.id}
        {...(state as ConverterUnitProps)}
        removeUnit={handleRemoveUnit}
        duplicateUnit={handleDuplicateUnit}
        addToReceipt={handleAddToReceipt}
        dragDrop={() => {}}
      />
    ) : null;
  });

  // ::::: JSX :::::
  return (
    <div className="calculator-page">
      <header>
        <figure>
          <img src={calcHeaderFigure} alt="calc-header-figure" />
        </figure>
        <h1 className="calculator-page__heading">calculator</h1>
        <div className="calculator-page__toggle">
          <SearchUnitTool callback={handleCreateUnit} />
          <ReceiptTool
            receiptData={receipts}
            deleteAllReceipts={handleDeleteAllReceipts}
            deleteReceipt={handleDeleteReceipt}
          />
        </div>
        <div className="calculator-page__toggle" style={{ paddingBlock: "0" }}>
          {/* Used for Delete All Calculators */}
          <MessageBoxTool
            windowTitle="Delete all Calculators"
            windowPrompt="Are you sure that you want to delete all Calculators?"
            windowButtonA={{ prompt: "Yes", color: "ERROR", bool: true }}
            windowButtonClose={{ bool: false }}
            windowButtonCallback={handleDeleteAllUnits}
            buttonText="Delete"
            faIcon={<FaIcons.FaTrash />}
          />
          {/* Used for Exporting of data */}
          <MessageBoxTool
            windowTitle="Export Data"
            windowPrompt="A text file will be created containing all your calculators and receipts!"
            windowButtonA={{ prompt: "Confirm", color: "NORMAL", bool: true }}
            windowButtonClose={{ bool: false }}
            windowButtonCallback={handleExportAll}
            buttonText="Export"
            faIcon={<FaIcons.FaUpload />}
          />
          <ImportCalculatorsTool importCalcs={handleImportAll} />
        </div>
      </header>
      {jsxUnits.length > 0 ? (
        <section className="calculator-page__container">{jsxUnits}</section>
      ) : (
        <figure className="calculator-page__empty-page">
          <img src={curvedArrowImg} alt="curved-arrow" />
        </figure>
      )}
    </div>
  );
}
